import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Save, Trash2, LayoutTemplate, GripVertical } from "lucide-react";
import { RichEditor } from "@/components/RichEditor";
import { useSettings } from "@/lib/useSettings";
import { useBranding } from "@/context/BrandingContext";

function brandColors(p) {
  const keys = ["heading_text", "body_text", "primary", "accent", "sidebar", "nav_active"];
  return [...new Set(["#000000", ...keys.map((k) => p?.[k]).filter(Boolean).map((c) => c.toUpperCase())])];
}

function PrintRequirements() {
  const { data, save } = useSettings();
  const { branding } = useBranding();
  const [v, setV] = useState(null);
  useEffect(() => { if (data) setV(data.print_requirements || ""); }, [data]);
  if (v === null) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mt-8" data-testid="print-requirements-card">
      <h2 className="font-display text-lg font-bold text-slate-800">Anforderungen an Druckunterlagen</h2>
      <p className="text-sm text-slate-500 mb-2">Erscheinen in den Mediadaten (PDF) – mit Überschriften, Listen und den Farben Ihres aktiven Farbprofils.</p>
      <RichEditor value={v} onChange={setV} headings={[1, 2, 3]} colors={brandColors(branding?.profile)} minHeight={180} testid="print-requirements-editor" />
      <Button onClick={() => save({ print_requirements: v })} className="mt-3 bg-[#0B132B] hover:bg-[#1C2541]" data-testid="print-requirements-save"><Save size={16} className="mr-1.5" /> Speichern</Button>
    </div>
  );
}

const NUM = [["width_mm", "Breite (mm)"], ["height_mm", "Höhe (mm)"], ["bleed_mm", "Beschnitt (mm)"], ["price", "Preis (€ netto)"]];
const blank = { name: "", width_mm: "", height_mm: "", bleed_mm: "", price: "", exclusive: false };
const toPayload = (r, i) => ({ name: r.name, order: i, exclusive: !!r.exclusive, ...Object.fromEntries(NUM.map(([k]) => [k, r[k] === "" || r[k] == null ? null : Number(r[k])])) });

function Row({ row, idx, onChange, onSave, onDelete, drag }) {
  return (
    <tr data-testid={`ad-format-row-${idx}`} draggable={!!row.id} onDragStart={() => drag.start(idx)}
      onDragOver={(e) => { e.preventDefault(); drag.over(idx); }} onDrop={(e) => { e.preventDefault(); drag.drop(); }} onDragEnd={drag.end}
      className={drag.overIdx === idx && drag.from !== null ? "bg-blue-50" : ""}>
      <td className="pl-2 py-2 w-6">
        {row.id ? <GripVertical size={16} className="text-slate-400 cursor-grab" data-testid={`ad-format-handle-${idx}`} /> : null}
      </td>
      <td className="px-3 py-2"><Input value={row.name} onChange={(e) => onChange({ ...row, name: e.target.value })} placeholder="z. B. 1/1 Seite" data-testid={`ad-format-name-${idx}`} /></td>
      {NUM.map(([k]) => (
        <td key={k} className="px-2 py-2"><Input type="number" value={row[k] ?? ""} onChange={(e) => onChange({ ...row, [k]: e.target.value })} className="w-24" data-testid={`ad-format-${k}-${idx}`} /></td>
      ))}
      <td className="px-2 py-2 text-center">
        <input type="checkbox" checked={!!row.exclusive} onChange={(e) => onChange({ ...row, exclusive: e.target.checked })} className="accent-slate-900 h-4 w-4" title="Kann nur an einen Partner vergeben werden" data-testid={`ad-format-exclusive-${idx}`} />
        {row.taken_by && <div className="text-[11px] text-slate-500 mt-0.5 whitespace-nowrap" data-testid={`ad-format-taken-${idx}`}>{row.taken_by.name}</div>}
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1 justify-end">
          <Button size="icon" variant="ghost" onClick={onSave} title="Speichern" data-testid={`ad-format-save-${idx}`}><Save size={16} className="text-slate-700" /></Button>
          <Button size="icon" variant="ghost" onClick={onDelete} title="Löschen" data-testid={`ad-format-delete-${idx}`}><Trash2 size={16} className="text-red-500" /></Button>
        </div>
      </td>
    </tr>
  );
}

export default function AdFormatsSettings() {
  const [rows, setRows] = useState([]);
  const [from, setFrom] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/ad-formats"); setRows(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const drop = async () => {
    if (from === null || overIdx === null || from === overIdx) { setFrom(null); return; }
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(overIdx, 0, moved);
    setRows(next); setFrom(null); setOverIdx(null);
    try { await api.put("/ad-formats/reorder", { ids: next.filter((r) => r.id).map((r) => r.id) }); toast.success("Reihenfolge gespeichert"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };
  const drag = { start: setFrom, over: setOverIdx, drop, end: () => { setFrom(null); setOverIdx(null); }, from, overIdx };

  const save = async (r, i) => {
    if (!r.name.trim()) { toast.error("Name ist erforderlich"); return; }
    try {
      if (r.id) await api.put(`/ad-formats/${r.id}`, toPayload(r, i));
      else await api.post("/ad-formats", toPayload(r, i));
      toast.success("Format gespeichert"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (r, i) => {
    if (!r.id) { setRows(rows.filter((_, j) => j !== i)); return; }
    if (!window.confirm(`Format „${r.name}“ löschen?`)) return;
    try { await api.delete(`/ad-formats/${r.id}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-5xl">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center text-violet-600"><LayoutTemplate size={22} /></div>
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Anzeigenformate</h1>
          <p className="text-slate-500 text-sm">Stehen bei Partnern (Freianzeige) und Anzeigenkunden zur Auswahl. Der Preis gilt für Anzeigenkunden. „Nur einmal belegbar“ (z. B. U2, U3, U4) erlaubt das Format bei genau einem aktuellen Partner. Reihenfolge per Maus am Griff verschieben.</p>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
        <table className="w-full text-sm" data-testid="ad-formats-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="w-6" /><th className="text-left px-3 py-3">Name</th>{NUM.map(([k, l]) => <th key={k} className="text-left px-2 py-3">{l}</th>)}<th className="px-2 py-3">Nur einmal belegbar</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r, i) => (
              <Row key={r.id || `new-${i}`} row={r} idx={i} onChange={(nr) => setRows(rows.map((x, j) => (j === i ? nr : x)))} onSave={() => save(r, i)} onDelete={() => remove(r, i)} drag={drag} />
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">Noch keine Formate angelegt.</td></tr>}
          </tbody>
        </table>
      </div>
      <Button onClick={() => setRows([...rows, { ...blank }])} variant="outline" className="mt-4" data-testid="add-ad-format-button"><Plus size={16} className="mr-1.5" /> Format hinzufügen</Button>
      <PrintRequirements />
    </div>
  );
}
